import { calculateSavingsPercentage } from './utils';
import { currencyCodeToLocale, localizedNumber, localizedPrice } from 'utils/localization';
import { useTranslation } from 'react-i18next';
import { CurrencyCode } from 'utils/localization';

type CalculationData = {
  currencyCode: CurrencyCode;
  calculationResult: {
    generatorUsageHours: number | null;
    dieselCostCents: number | null;
    gridElectricityCostCents: number | null;
    totalCostEstimateCents: number;
    emissionsCo2Tons: number | null;
    emissionsNoxKg: number | null;
    rentalCostCents: number | null;
    additionalCostCents: number | null;
  };
  costAssumptions: {
    gensetRentalCostCents: number;
    batteryRentalCostCents: number | null;
    additionalCostsCents: number | null;
  };
};

type TableRowData = {
  label: string;
  baseValue: string;
  hybridValue: string;
  savings: string;
};

type TableSections = {
  costs: TableRowData[];
  environmental: TableRowData[];
};

export const generateTableData = (
  t: ReturnType<typeof useTranslation>['t'],
  baseCalculation: CalculationData,
  hybridCalculation: CalculationData,
  isEndDateProvided: boolean = false
): TableSections => {
  const costEstimatesLabel = isEndDateProvided
    ? t('blueprints:details.table.projectCostEstimates')
    : t('blueprints:details.table.weeklyCostEstimates');

  return {
    costs: [
      {
        label: t('blueprints:details.table.rentalCosts'),
        baseValue: localizedPrice({
          value: baseCalculation.calculationResult.rentalCostCents! / 100,
          locale: currencyCodeToLocale(baseCalculation.currencyCode),
        }),
        hybridValue: localizedPrice({
          value: hybridCalculation.calculationResult.rentalCostCents! / 100,
          locale: currencyCodeToLocale(hybridCalculation.currencyCode),
        }),
        savings: calculateSavingsPercentage(
          baseCalculation.calculationResult.rentalCostCents,
          hybridCalculation.calculationResult.rentalCostCents
        ),
      },
      {
        label: t('blueprints:details.table.additionalCosts'),
        baseValue: localizedPrice({
          value: baseCalculation.calculationResult.additionalCostCents! / 100,
          locale: currencyCodeToLocale(baseCalculation.currencyCode),
        }),
        hybridValue: localizedPrice({
          value: hybridCalculation.calculationResult.additionalCostCents! / 100,
          locale: currencyCodeToLocale(hybridCalculation.currencyCode),
        }),
        savings: calculateSavingsPercentage(
          baseCalculation.calculationResult.additionalCostCents,
          hybridCalculation.calculationResult.additionalCostCents
        ),
      },
      {
        label: t('blueprints:details.table.dieselCosts'),
        baseValue: localizedPrice({
          value: baseCalculation.calculationResult.dieselCostCents! / 100,
          locale: currencyCodeToLocale(baseCalculation.currencyCode),
        }),
        hybridValue: localizedPrice({
          value: hybridCalculation.calculationResult.dieselCostCents! / 100,
          locale: currencyCodeToLocale(hybridCalculation.currencyCode),
        }),
        savings: calculateSavingsPercentage(
          baseCalculation.calculationResult.dieselCostCents,
          hybridCalculation.calculationResult.dieselCostCents
        ),
      },
      {
        label: t('blueprints:details.table.electricityCosts'),
        baseValue: localizedPrice({
          value: baseCalculation.calculationResult.gridElectricityCostCents! / 100,
          locale: currencyCodeToLocale(baseCalculation.currencyCode),
        }),
        hybridValue: localizedPrice({
          value: hybridCalculation.calculationResult.gridElectricityCostCents! / 100,
          locale: currencyCodeToLocale(hybridCalculation.currencyCode),
        }),
        savings: calculateSavingsPercentage(
          baseCalculation.calculationResult.gridElectricityCostCents,
          hybridCalculation.calculationResult.gridElectricityCostCents
        ),
      },
      {
        label: costEstimatesLabel,
        baseValue: localizedPrice({
          value: baseCalculation.calculationResult.totalCostEstimateCents! / 100,
          locale: currencyCodeToLocale(baseCalculation.currencyCode),
        }),
        hybridValue: localizedPrice({
          value: hybridCalculation.calculationResult.totalCostEstimateCents! / 100,
          locale: currencyCodeToLocale(hybridCalculation.currencyCode),
        }),
        savings: calculateSavingsPercentage(
          baseCalculation.calculationResult.totalCostEstimateCents,
          hybridCalculation.calculationResult.totalCostEstimateCents
        ),
      },
    ],
    environmental: [
      {
        label: t('blueprints:details.table.generatorUsage'),
        baseValue: localizedNumber({
          value: baseCalculation.calculationResult.generatorUsageHours,
          unitLabel: t('blueprints:details.table.units.hours'),
          locale: currencyCodeToLocale(baseCalculation.currencyCode),
          fractionDigits: 1,
        }),
        hybridValue: localizedNumber({
          value: hybridCalculation.calculationResult.generatorUsageHours,
          unitLabel: t('blueprints:details.table.units.hours'),
          locale: currencyCodeToLocale(hybridCalculation.currencyCode),
          fractionDigits: 1,
        }),
        savings: calculateSavingsPercentage(
          baseCalculation.calculationResult.generatorUsageHours,
          hybridCalculation.calculationResult.generatorUsageHours
        ),
      },
      {
        label: t('blueprints:details.table.emissionsCo2'),
        baseValue: localizedNumber({
          value: baseCalculation.calculationResult.emissionsCo2Tons,
          unitLabel: t('blueprints:details.table.units.tonnes'),
          locale: currencyCodeToLocale(baseCalculation.currencyCode),
          fractionDigits: 1,
        }),
        hybridValue: localizedNumber({
          value: hybridCalculation.calculationResult.emissionsCo2Tons,
          unitLabel: t('blueprints:details.table.units.tonnes'),
          locale: currencyCodeToLocale(hybridCalculation.currencyCode),
          fractionDigits: 1,
        }),
        savings: calculateSavingsPercentage(
          baseCalculation.calculationResult.emissionsCo2Tons,
          hybridCalculation.calculationResult.emissionsCo2Tons
        ),
      },
      {
        label: t('blueprints:details.table.emissionsNox'),
        baseValue: localizedNumber({
          value: baseCalculation.calculationResult.emissionsNoxKg,
          unitLabel: 'kg',
          locale: currencyCodeToLocale(baseCalculation.currencyCode),
          fractionDigits: 1,
        }),
        hybridValue: localizedNumber({
          value: hybridCalculation.calculationResult.emissionsNoxKg,
          unitLabel: 'kg',
          locale: currencyCodeToLocale(hybridCalculation.currencyCode),
          fractionDigits: 1,
      }),
        savings: calculateSavingsPercentage(
          baseCalculation.calculationResult.emissionsNoxKg,
          hybridCalculation.calculationResult.emissionsNoxKg
        ),
      },
    ],
  };
};
